import {FC, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useAuth} from '../auth'
import {AuthAuroraShell, AuthBrandHeader} from '../auth/components/AuthAurora'
import {
  CompanyProfileForm,
  finishOnboarding,
  setOnboardingDismissed,
  updateCompanyProfile,
} from './core/_requests'

// Vídeo de apresentação do produto (overview mais recente, confirmado em 2026-09-20).
const WELCOME_VIDEO_URL = 'https://www.youtube.com/embed/2Rr03-TBxB8?rel=0'

// Mesma lista do cadastro antigo (signup), para os relatórios por segmento seguirem comparáveis.
const SEGMENTS = [
  'Agências de Marketing e Publicidade',
  'Tecnologia da Informação (TI)',
  'Consultoria',
  'Serviços Criativos (Design Gráfico, Fotografia, Vídeo)',
  'Arquitetura e Design de Interiores',
  'Construção Civil e Engenharia',
  'E-commerce',
  'Eventos',
  'Serviços Financeiros',
  'Indústria e Manufatura',
  'Startups',
  'Outro',
]

const TEAM_SIZES = [
  {value: '1', label: 'Só eu'},
  {value: '2-5', label: '2 a 5 pessoas'},
  {value: '6-10', label: '6 a 10 pessoas'},
  {value: '11-20', label: '11 a 20 pessoas'},
  {value: '21-50', label: '21 a 50 pessoas'},
  {value: '51+', label: 'Mais de 50 pessoas'},
]

// Boas-vindas do primeiro acesso: uma tela só, sem cadastro. O resto vive no checklist do dashboard.
// Substitui o wizard de 5 a 6 passos (stepper do Metronic), que replicava telas que já existem.
export const WelcomePage: FC = () => {
  const {currentUser, setCurrentUser} = useAuth()
  const navigate = useNavigate()
  const [showVideo, setShowVideo] = useState(false)
  const [loading, setLoading] = useState<'start' | 'explore' | null>(null)
  const [error, setError] = useState('')

  const firstName = (currentUser?.name ?? '').trim().split(' ')[0]
  const isManagerLike = !!currentUser?.administrator || !!currentUser?.manager
  const company = currentUser?.instance

  // O cadastro mínimo cria a empresa só com o nome da pessoa: o administrador completa aqui.
  // Tudo opcional; o nome já vem preenchido.
  const askCompanyProfile = !!currentUser?.administrator && !!company?.id
  const initialProfile: CompanyProfileForm = {
    name: company?.name ?? '',
    segment: company?.segment ?? '',
    team_size: company?.team_size ?? '',
    phone: company?.phone ?? '',
  }
  const [profile, setProfile] = useState<CompanyProfileForm>(initialProfile)
  const profileChanged = (Object.keys(profile) as Array<keyof CompanyProfileForm>).some(
    (key) => profile[key].trim() !== initialProfile[key].trim()
  )
  const setField = (key: keyof CompanyProfileForm, value: string) =>
    setProfile((current) => ({...current, [key]: value}))

  // "Começar" leva ao dashboard com o checklist. "Explorar sozinho" também, mas com o checklist
  // oculto (ele volta em Ajuda > Primeiros passos).
  const enter = async (mode: 'start' | 'explore') => {
    setLoading(mode)
    setError('')

    if (askCompanyProfile && profileChanged && !profile.name.trim()) {
      setError('Informe o nome da empresa.')
      setLoading(null)
      return
    }

    try {
      if (askCompanyProfile && profileChanged) {
        await updateCompanyProfile(Number(company!.id), profile)
      }
      await finishOnboarding()
      if (mode === 'explore') await setOnboardingDismissed(true)
      setCurrentUser({
        ...currentUser!,
        onboarded: 1,
        instance:
          askCompanyProfile && profileChanged
            ? {
                ...company!,
                name: profile.name.trim(),
                segment: profile.segment || null,
                team_size: profile.team_size || null,
                phone: profile.phone.trim() || undefined,
              }
            : company,
      })
      navigate('/dashboard', {replace: true})
    } catch (e) {
      console.error(e)
      setError('Não foi possível continuar agora. Tente novamente em instantes.')
      setLoading(null)
    }
  }

  return (
    <AuthAuroraShell>
      <AuthBrandHeader />

      <div className='text-center mb-8'>
        <h1 className='text-dark fw-bolder mb-3'>Olá, {firstName}!</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          {isManagerLike
            ? 'Em poucos minutos você terá o primeiro projeto rodando.'
            : 'Em poucos minutos você lança a sua primeira hora.'}{' '}
          Vamos por partes, no seu ritmo.
        </div>
      </div>

      {showVideo ? (
        <div className='ratio ratio-16x9 mb-8 onboarding-welcome-video'>
          <iframe
            src={WELCOME_VIDEO_URL}
            title='Apresentação do TaskRush'
            allow='accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
            referrerPolicy='strict-origin-when-cross-origin'
            allowFullScreen
          />
        </div>
      ) : (
        <div className='text-center mb-8'>
          <button
            type='button'
            className='btn btn-link link-primary fw-semibold'
            onClick={() => setShowVideo(true)}
          >
            <i className='bi bi-play-circle me-2'></i>
            Assistir à apresentação do TaskRush
          </button>
        </div>
      )}

      {askCompanyProfile && (
        <div className='mb-8'>
          <div className='fw-bold fs-6 text-dark mb-4'>Conte um pouco sobre a sua empresa</div>
          <div className='mb-4'>
            <label className='form-label fs-7 text-gray-700' htmlFor='onboarding-company-name'>
              Nome da empresa
            </label>
            <input
              id='onboarding-company-name'
              type='text'
              className='form-control bg-transparent'
              maxLength={255}
              autoComplete='organization'
              value={profile.name}
              onChange={(e) => setField('name', e.target.value)}
            />
          </div>
          <div className='row g-4 mb-4'>
            <div className='col-sm-6'>
              <label className='form-label fs-7 text-gray-700' htmlFor='onboarding-segment'>
                Segmento
              </label>
              <select
                id='onboarding-segment'
                className='form-select bg-transparent'
                value={profile.segment}
                onChange={(e) => setField('segment', e.target.value)}
              >
                <option value=''>Selecione</option>
                {SEGMENTS.map((segment) => (
                  <option key={segment} value={segment}>
                    {segment}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-sm-6'>
              <label className='form-label fs-7 text-gray-700' htmlFor='onboarding-team-size'>
                Tamanho da equipe
              </label>
              <select
                id='onboarding-team-size'
                className='form-select bg-transparent'
                value={profile.team_size}
                onChange={(e) => setField('team_size', e.target.value)}
              >
                <option value=''>Selecione</option>
                {TEAM_SIZES.map((size) => (
                  <option key={size.value} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className='form-label fs-7 text-gray-700' htmlFor='onboarding-phone'>
              WhatsApp ou telefone (opcional)
            </label>
            <input
              id='onboarding-phone'
              type='tel'
              className='form-control bg-transparent'
              maxLength={30}
              autoComplete='tel'
              value={profile.phone}
              onChange={(e) => setField('phone', e.target.value)}
            />
          </div>
        </div>
      )}

      {error && (
        <div className='mb-8 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{error}</div>
        </div>
      )}

      <div className='d-grid gap-3'>
        <button
          type='button'
          className='btn btn-primary'
          onClick={() => enter('start')}
          disabled={loading !== null}
        >
          <span className='indicator-label'>Começar</span>
          {loading === 'start' && (
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          )}
        </button>
        <button
          type='button'
          className='btn btn-light'
          onClick={() => enter('explore')}
          disabled={loading !== null}
        >
          <span className='indicator-label'>Explorar sozinho</span>
          {loading === 'explore' && (
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          )}
        </button>
      </div>

      <div className='text-gray-500 fs-7 text-center mt-6'>
        Os primeiros passos ficam sempre disponíveis em Ajuda.
      </div>
    </AuthAuroraShell>
  )
}
